import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import * as moment from 'moment';
import { ConfigService } from '../config.service';
import { HomeService } from '../home/home.service';
import { ResourceService } from '../resource/resource.service';
import { appConfig } from '../../app.config';
import { environment } from '../../../environments/environment';

/**
 * Admin media / channel API used by `add-channel` (legacy admin parity).
 */
@Injectable({ providedIn: 'root' })
export class MediaService {
  constructor(
    private resourceService: ResourceService,
    private configService: ConfigService,
    private homeService: HomeService,
  ) {}

  private get root(): string {
    return `${this.configService.api}admin-service/api/v1`;
  }

  /** Backend expects `searchKey` in lowercase for catalog list APIs. */
  private normalizeSearchKey(search: string | null | undefined): string {
    if (search == null) {
      return '';
    }
    return String(search).trim().toLowerCase();
  }

  getMediaById(id: string | number): Observable<any> {
    return this.homeService.getAdminMediaItemById(Number(id));
  }

  updateMediaItem(id: string | number, body: any, _type: string): Observable<any> {
    return this.homeService.updateAdminMediaItem(Number(id), body);
  }

  getSchedulingChannelInfoWithDate(
    date: string,
    mediaItemID: string | number,
    isForM3u8: boolean,
    isInitial: boolean,
  ): Observable<any> {
    const url = `${this.root}/schedulingChannelInfo?date=${date}&mediaItemId=${mediaItemID}&isForM3U8=${isForM3u8}&isInitial=${isInitial}`;
    return this.resourceService.getData(url);
  }

  /**
   * Admin catalog list. When `forAffordableChannel` is true (INEXPENSIVE_CHANNEL editor),
   * backend expects `forAffordableChannel=true` on the query string.
   */
  getMediaItems(
    page: number,
    size: number,
    search: string,
    itemType: string,
    _unused: string,
    _flag: boolean,
    forAffordableChannel = false,
  ): Observable<any> {
    let url = `${this.root}/mediaItem/getAllMediaItemList?itemType=${encodeURIComponent(itemType)}&page=${page}&size=${size}`;
    if (forAffordableChannel) {
      url += `&forAffordableChannel=true`;
    }
    const searchKey = this.normalizeSearchKey(search);
    if (searchKey) {
      url += `&searchkey=${encodeURIComponent(searchKey)}`;
    }
    return this.resourceService.getData(url);
  }

  getMediaSeries(page: number, size: number, search: string, seriesType: string): Observable<any> {
    let url = `${this.root}/mediaseries/getAllMediaSeriesList?page=${page}&size=${size}&seriesType=${encodeURIComponent(seriesType)}`;
    const searchKey = this.normalizeSearchKey(search);
    if (searchKey) {
      url += `&searchkey=${encodeURIComponent(searchKey)}`;
    }
    return this.resourceService.getData(url);
  }

  getM3u8MediaItemById(id: string | number): Observable<any> {
    return this.resourceService.getData(`${this.root}/m3u8ScheduleItem/${id}`);
  }

  deleteM3u8Item(id: string | number): Observable<any> {
    return this.resourceService.deleteData(`${this.root}/m3u8ScheduleItem/${id}`);
  }

  checkStatus(streamId: string): Observable<any> {
    const url = `${this.root}/schedulingChannelInfo/streamStatus?appName=ChannelApp&streamId=${encodeURIComponent(streamId)}`;
    return this.resourceService.getData(url);
  }

  getMediaSeriesById(id: string | number): Observable<any> {
    return this.resourceService.getData(`${this.root}/mediaseries/${id}`);
  }

  /** Update media series (title, contents order, publish state, artworks). */
  updateMediaSeries(id: string | number, body: any, _actionType?: string): Observable<any> {
    return this.homeService.updateAdminMediaSeries(Number(id), body);
  }

  createMediaItem(body: any): Observable<any> {
    return this.homeService.createAdminMediaItem(body);
  }

  /** DELETE catalog media item(s) by id (`DELETE .../mediaItem/delete?ids=`). */
  deleteMediaItems(ids: number[]): Observable<any> {
    return this.homeService.deleteAdminMediaItems(ids);
  }

  /** DELETE media series (`DELETE …/mediaseries?ids=`). */
  deleteMediaSeries(id: number): Observable<any> {
    return this.homeService.deleteAdminMediaSeries([id]);
  }

  /** DELETE catalog folder by id. */
  deleteCatalog(id: number): Observable<any> {
    return this.homeService.deleteAdminCatalog(id);
  }

  /**
   * Remove media item(s) from a specific media series — unlinks them from the
   * series without deleting the underlying media item records.
   * `DELETE .../mediaItem/deleteMediaItemFromMediaSeries?ids=&mediaSeriesId=`.
   */
  deleteMediaItemsFromSeries(ids: number[], mediaSeriesId: number): Observable<any> {
    return this.homeService.deleteAdminMediaItemsFromSeries(ids, mediaSeriesId);
  }

  getM3uDataByCatagory(
    mediaItemId: string | number,
    categoryName: string,
    m3uType: string,
    page: number,
    size: number,
  ): Observable<any> {
    return this.homeService.getM3uDataByCatagory(Number(mediaItemId), categoryName, m3uType, true, page, size);
  }

  searchM3uData(
    id: string | number,
    search: string | null,
    categoryName: string | null,
    m3uType: string,
  ): Observable<any> {
    return this.homeService.searchM3uData(Number(id), search, categoryName, m3uType, true);
  }

  refreshM3uData(mediaItemId: string | number): Observable<any> {
    return this.resourceService.postData(`${this.root}/mediaItem/${mediaItemId}/refreshM3u`, {});
  }

  getM3uData(mediaItemId: any, m3uType: any, page: any, size: any): Observable<any> {
    const date = moment().format('YYYY-MM-DD');
    return this.homeService.getM3uData(mediaItemId, m3uType, date, page, size, 50, true);
  }

  updateM3u8Item(payload: any, m3u8EditId: string | number): Observable<any> {
    return this.resourceService.putData(`${this.root}/m3u8ScheduleItem/${m3u8EditId}`, payload);
  }

  createM3u8Item(payload: any, mediaItemId: string | number): Observable<any> {
    return this.resourceService.postData(`${this.root}/mediaItem/${mediaItemId}/m3u8ScheduleItem`, payload);
  }

  /**
   * Paginated M3U8 playlist items for admin (catalog / scheduling picker).
   * Uses `mediaItem/getAllM3U8PlaylistItems` (not legacy `m3u8ScheduleItem?page=&size=`).
   * Optional `search` → `searchkey` query param (same convention as `getAllMediaItemList`).
   */
  getMediaItemsM3u8(page: number, size: number, search: string | null | undefined = null): Observable<any> {
    let url = `${this.root}/mediaItem/getAllM3U8PlaylistItems?page=${page}&size=${size}`;
    const searchKey = this.normalizeSearchKey(search == null ? '' : String(search));
    if (searchKey) {
      url += `&searchkey=${encodeURIComponent(searchKey)}`;
    }
    return this.resourceService.getData(url);
  }

  getItemAnalytics(
    mediaItemId: string | number,
    page: number,
    size: number,
    startDate: string,
    endDate: string,
  ): Observable<any> {
    const url = `${this.root}/viewerAnalytics/getItemAnalytics?mediaItemId=${mediaItemId}&page=${page}&size=${size}&startDate=${startDate}&endDate=${endDate}`;
    return this.resourceService.getData(url);
  }

  getItemAnalyticsByDateRange(
    mediaItemId: string | number,
    startDate: string,
    endDate: string,
    patternType: string,
  ): Observable<any> {
    const url = `${this.root}/viewerAnalytics/getItemAnalyticsByDateRange?mediaItemId=${mediaItemId}&startDate=${startDate}&endDate=${endDate}&patternType=${encodeURIComponent(
      patternType,
    )}`;
    return this.resourceService.getData(url);
  }

  getItemAnalyticsCumulative(mediaItemId: string | number, startDate: string, endDate: string): Observable<any> {
    const url = `${this.root}/viewerAnalytics/getItemAnalyticsCumulative?mediaItemId=${mediaItemId}&startDate=${startDate}&endDate=${endDate}`;
    return this.resourceService.getData(url);
  }

  // Legacy pagination API retained for compatibility with existing UI calls.
  getMediaPlaysGraphForDayWithPagination(
    startDate: string,
    endDate: string,
    page: number,
    size: number,
    _isCumulative: boolean,
  ): Observable<any> {
    const url = `${this.root}/viewerAnalytics/getMediaPlaysGraphForDay?startDate=${startDate}&endDate=${endDate}&page=${page}&size=${size}`;
    return this.resourceService.getData(url);
  }

  /**
   * Replaces playlist for a channel day. Backend expects PUT on schedulingChannelInfo (not POST saveChannelPlaylist).
   * Example: PUT .../schedulingChannelInfo?channelId=62833&date=2026-04-03&playlistType=DAILY&isForM3U8=false
   */
  updateSchedulingChannelPlaylist(
    channelId: any,
    date: string,
    playlistType: string,
    dataToSend: any,
    isForM3u8: boolean,
  ): Observable<any> {
    const url = `${this.root}/schedulingChannelInfo?channelId=${channelId}&date=${date}&playlistType=${encodeURIComponent(
      playlistType,
    )}&isForM3U8=${isForM3u8}`;
    return this.resourceService.putData(url, dataToSend);
  }

  startStopAutomation(channelInfoId: any, startAutomation: boolean): Observable<any> {
    const url =
      `${this.root}/schedulingChannelInfo/automation` +
      `?channelInfoId=${encodeURIComponent(String(channelInfoId))}` +
      `&startAutomation=${startAutomation}`;
    return this.resourceService.putData(url, {});
  }

  uploadChunkVideo(data: any): Observable<any> {
    const url = `${environment.dashboard}${appConfig.upload}/video/chunk`;
    return this.resourceService.postNewData(url, data);
  }

  uploadVedioFile(formData: any, type: string): Observable<any> {
    const uploadFileUrl = `${environment.dashboard}${appConfig.upload}/${type}`;
    return this.resourceService.postNewData(uploadFileUrl, formData);
  }

  uploadFile(formData: any, type: string): Observable<any> {
    const uploadFileUrl = `${environment.dashboard}${appConfig.upload}/${type}`;
    return this.resourceService.postNewData(uploadFileUrl, formData);
  }

  uploadBase64(data: any): Observable<any> {
    const url = `${environment.dashboard}${appConfig.upload}/base64images`;
    return this.resourceService.postData(url, data);
  }

  deleteMediaDoc(mediaItemId: number, itemId: number, type: string, isUrl: boolean, isTvLogo: boolean): Observable<any> {
    let url = `${this.root}/mediaItem`;
    if (type === 'video') {
      url += `/${mediaItemId}${isUrl ? '/videoUrl' : `/video/${itemId}`}`;
    } else if (type === 'audio') {
      url += `/${mediaItemId}${isUrl ? '/audioUrl' : `/audio/${itemId}`}`;
    } else if (type === 'document') {
      if (isTvLogo) {
        url += `/${mediaItemId}/tvLogo`;
      } else {
        url += `/document/?documentId=${encodeURIComponent(String(itemId))}&mediaItemId=${encodeURIComponent(String(mediaItemId))}`;
      }
    }
    return this.resourceService.deleteData(url);
  }

  deleteMediaVideo(mediaItemId: number, videoId: number | null, isVideoUrl: boolean): Observable<any> {
    let url = `${this.root}/mediaItem/video/${mediaItemId}?isVideoUrl=${isVideoUrl}`;
    if (!isVideoUrl && videoId != null) {
      url += `&videoId=${encodeURIComponent(String(videoId))}`;
    }
    return this.resourceService.deleteData(url);
  }

  deleteMediaAudio(mediaItemId: number, audioId: number | null, isAudioUrl: boolean): Observable<any> {
    let url = `${this.root}/mediaItem/audio/${mediaItemId}?isAudioUrl=${isAudioUrl}`;
    if (!isAudioUrl && audioId != null) {
      url += `&audioId=${encodeURIComponent(String(audioId))}`;
    }
    return this.resourceService.deleteData(url);
  }

  getOriginalVideoUrl(id: number): Observable<any> {
    return this.resourceService.getData(`${this.root}/video/${id}/originalVideoUrl`);
  }

  downloadVideoFromBlob(id: number): Observable<any> {
    return this.resourceService.getData(`${this.root}/video/${id}/download`);
  }

  /**
   * Stock / library images for artwork picker (`uploadStock`).
   * - `stockType=LIBERARY` + `organixationId` — org library (when organizationId set).
   * - `stockType=STOCK` — global stock catalog (no organization param; matches dashboard curl).
   */
  getUploadStock(params: {
    artworkIdList?: number | string | null;
    artworkType: 'SQUARE' | 'WIDE' | 'BANNER';
    organizationId?: string | number | null;
    page?: number;
    size?: number;
    stockType?: string;
  }): Observable<any> {
    const page = params.page ?? 1;
    const size = params.size ?? 10;
    const stockType = params.stockType ?? 'LIBERARY';
    const q = new URLSearchParams();
    if (params.artworkIdList != null && String(params.artworkIdList).trim() !== '') {
      q.set('artworkIdList', String(params.artworkIdList));
    }
    q.set('artworkType', params.artworkType);
    const org = params.organizationId;
    if (org != null && String(org).trim() !== '') {
      q.set('organixationId', String(org));
    }
    q.set('page', String(page));
    q.set('size', String(size));
    q.set('stockType', stockType);
    return this.resourceService.getData(`${this.root}/uploadStock?${q.toString()}`);
  }

  /**
   * Register an uploaded document in the org/library stock (`uploadStock` POST).
   */
  postUploadStock(body: {
    artworkType: 'SQUARE' | 'WIDE' | 'BANNER';
    documentId: number;
    parentId: number | null;
    /** Server expects `LIBERARY` spelling for library uploads. */
    type: string;
  }): Observable<any> {
    return this.resourceService.postData(`${this.root}/uploadStock`, body);
  }

  deleteUploadStock(id: number): Observable<any> {
    return this.resourceService.deleteData(`${this.root}/uploadStock/${id}`);
  }
}
